import React, {useEffect, useState} from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../styles/colors';
import {useLocation, useNavigate} from 'react-router-dom';
import DefaultButton from 'components/common/DefaultButton';
import ScreenLayout, {logout} from 'components/common/ScreenLayout';
import {getDevice} from 'utils/getDevice';
import NarrowHeader from 'components/common/NarrowHeader';
import Footer from 'components/common/Footer';
import CommonModal from 'components/common/CommonModal';
import PhoneVerification from 'components/register/PhoneVerification';
import {ICountryArray, countryArray} from 'data/country';
import {IModalState, IRegisterInfo} from './Register';
import PasswordSetting from 'components/register/PasswordSetting';
import {minmax} from 'utils/minmax';
import {resetPassword} from 'function/api';
import {useSetRecoilState} from 'recoil';
import {userInfoState} from 'utils/Atoms';

/**
 * 초기 회원가입 정보
 * 비밀번호 재설정 폼의 초기값을 정의합니다.
 */
const initialRegister = {
  phone: '',
  verifiCode: '',
  password: '',
  checkPassword: '',
  name: '',
  birthDay: '',
  gender: '',
  nationality: '',
  service: false, // 서비스 약관
  privacy: false, // 개인정보 약관
  location: false, // 위치기반 약관
};

/**
 * 비밀번호 재설정 페이지 컴포넌트
 * 비밀번호 재설정 및 휴대폰 번호 변경을 처리하는 페이지입니다.
 * @returns {JSX.Element} 비밀번호 재설정 페이지 컴포넌트
 */
const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  // Visible Password State

  const [errorModal, setErrorModal] = useState({});

  const navigate = useNavigate();
  const {state} = useLocation();
  const device = getDevice();

  const setUserInfo = useSetRecoilState(userInfoState);

  const [selectCountry, setSelectCountry] = useState<ICountryArray>(
    countryArray[0],
  );

  // 다음으로 or 가입하기 활성화
  const [enableButton, setEnableButton] = useState(false);

  // 인증번호 전송 버튼 텍스트
  const [authSendButtonTitle, setAuthSendButtonTitle] = useState('sendAuth');

  const [registerInfo, setRegisterInfo] =
    useState<IRegisterInfo>(initialRegister);

  // 모달
  const initModalState: IModalState = {
    confirm: {state: '', open: false},
    tempInfo: {state: '', open: false, info: {}},
  };
  const [modalState, setModalState] = useState<IModalState>(initModalState);

  // 인증 코드 인증 여부
  const [checkedAuth, setCheckedAuth] = useState<boolean>(false);

  // step
  const [step, setStep] = useState(0);
  const steps = [
    {
      // 휴대폰 번호 입력
      title: 'mobilePhoneVerification',
      component: (
        <PhoneVerification
          key={0}
          selectCountry={selectCountry}
          setSelectCountry={setSelectCountry}
          registerInfo={registerInfo}
          setRegisterInfo={setRegisterInfo}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
          setEnableButton={setEnableButton}
          setAuthSendButtonTitle={setAuthSendButtonTitle}
          authSendButtonTitle={authSendButtonTitle}
          codePurpose="RESET_PASSWORD"
          setModalState={setModalState}
          setCheckedAuth={setCheckedAuth}
          checkedAuth={checkedAuth}
        />
      ),
    },
    {
      // 비밀번호 설정
      title: 'setPassword',
      component: (
        <PasswordSetting
          key={1}
          registerInfo={registerInfo}
          setRegisterInfo={setRegisterInfo}
          setEnableButton={setEnableButton}
          reset
        />
      ),
    },
    {
      // 휴대폰 번호 입력
      title: 'changePhoneNumber',
      component: (
        <PhoneVerification
          key={0}
          selectCountry={selectCountry}
          setSelectCountry={setSelectCountry}
          registerInfo={registerInfo}
          setRegisterInfo={setRegisterInfo}
          setIsLoading={setIsLoading}
          setErrorModal={setErrorModal}
          setEnableButton={setEnableButton}
          setAuthSendButtonTitle={setAuthSendButtonTitle}
          authSendButtonTitle={authSendButtonTitle}
          codePurpose="RESET_PASSWORD"
          setModalState={setModalState}
          setCheckedAuth={setCheckedAuth}
          checkedAuth={checkedAuth}
          phoneNumberLabel="newPhoneNumberId"
        />
      ),
    },
  ];

  // 다음으로 or 완료 버튼 스타일
  const buttonStyle = {
    width: '100%',
    height: '60px',
    backgroundColor: enableButton ? colors.primary2 : '#ededed',
  };

  useEffect(() => {
    // 휴대폰 번호 변경
    if (state?.type && state?.type === 'changePhoneNumber') {
      setStep(2);
    }
  }, []);

  return (
    <ScreenLayout isLoading={isLoading} noHeader={true} errorModal={errorModal}>
      <Container>
        <Main $device={device}>
          <InputField>
            {/* 헤더 */}
            <NarrowHeader title={steps[step].title} />
            {/* 정보 입력 컴포넌트 */}
            <InputContainer step={step}>{steps[step].component}</InputContainer>
          </InputField>

          {/* 다음으로 버튼 */}
          <DefaultButton
            text={step === 0 ? 'next' : 'complete'}
            buttonStyle={buttonStyle}
            disabled={!enableButton}
            textStyle={{
              color: enableButton ? colors.white : colors.borderGray,
              fontSize: minmax(20, 16),
            }}
            onClick={async () => {
              if (enableButton) {
                if (step === 0) {
                  setStep(1);
                } else if (step === 1) {
                  const response = await resetPassword({
                    phone: registerInfo.phone,
                    password: registerInfo.password,
                    setIsLoading,
                    setErrorModal,
                  });
                  if (response) {
                    // 작성 중인 화면 이동 모달 오픈
                    setModalState
                      ? setModalState(prev => ({
                          ...prev,
                          confirm: {
                            open: true,
                            state: '',
                          },
                        }))
                      : null;
                  }
                } else if (step === 2) {
                  // 로그인 후 다른 페이지에서 휴대폰 변경하기로 넘어올 때
                  if (state?.currentData) {
                    const phone = {
                      phone: registerInfo.phone,
                      locale: selectCountry.languageCode,
                    };
                    state.movePath &&
                      navigate(`/${state.movePath}`, {
                        state: {
                          // selectCountry,
                          currentData: state.currentData,
                          [state.isEmergencyContact
                            ? 'emergencyContact'
                            : 'phone']: phone,
                          userUUID:
                            state.currentData.userUUID ||
                            state.currentData.uuid ||
                            '',
                        },
                      });
                  }
                }
              }
            }}
          />
        </Main>
      </Container>
      <Footer isNarrow />

      {/* 비밀번호 재설정 완료 모달 */}
      {modalState.confirm.open && (
        <CommonModal
          title={'completeSetPassword'}
          detail={'againLogin'}
          firstButtonText={'confirm'}
          firstButtonClick={() => {
            setModalState(initModalState);
            // logout({
            //   setUserInfo,
            //   navigate,
            // });
          }}
          closeModal={() => {
            setModalState(initModalState);
            // logout({
            //   setUserInfo,
            //   navigate,
            // });
          }}
        />
      )}
      {/* 비밀번호 미입력 상태 안내 모달 */}
      {modalState.tempInfo.open && (
        <CommonModal
          title={'tempInfoModalTitle'}
          detail={'tempInfoModalDetail'}
          firstButtonText={'confirm'}
          firstButtonClick={() => {
            navigate('/register', {
              state: {step: 1, info: modalState.tempInfo.info},
            });
          }}
          closeModal={() => {
            navigate('/register', {
              state: {step: 1, info: modalState.tempInfo.info},
            });
          }}
        />
      )}
    </ScreenLayout>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const Main = styled.div<{$device?: string}>`
  display: flex;
  width: 680px;
  flex-direction: column;
  align-items: center;
  ${(props: any) =>
    props.$device === 'mobile' &&
    css`
      width: 90%;
    `}
`;

const InputField = styled.div<{$device?: string}>`
  width: 100%;
  margin-bottom: 40px;
  padding-top: 34px;
  padding-bottom: max(20px, min(2vw, 40px));
  border: 1px solid ${colors.borderGray};
  border-radius: ${minmax(10)};
  margin-top: ${minmax(30)};
`;

const InputContainer = styled.div<{step?: number}>`
  margin: ${props =>
    props.step === 4
      ? 0
      : 'max(20px, min(2vw, 40px)) max(20px, min(2vw, 40px)) 0'};
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  input:focus {
    border: ${props => (props.step === 0 ? '1px solid #808080' : '')};
  }
`;

export default ResetPassword;
